import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

const statusColors = {
  approved: 'success',
  rejected: 'error',
  pending: 'warning',
  under_review: 'warning',
  completed: 'success'
};

const statusLabels = {
  approved: 'Одобрено',
  rejected: 'Отклонено',
  pending: 'На проверке',
  under_review: 'На проверке',
  completed: 'Завершено'
};

export default function SurveyHistory() {
  const [currentTab, setCurrentTab] = useState('all');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchSurveyHistory();
  }, []);

  const fetchSurveyHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys/user/responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching survey history:', err);
      setError('Failed to load survey history');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const filteredSurveys = surveys.filter((survey) => {
    if (currentTab === 'all') return true;
    if (currentTab === 'pending') return survey.status === 'pending' || survey.status === 'under_review';
    return survey.status === currentTab;
  });

  const handleDownload = (surveyId) => {
    // TODO: Implement survey response download
    console.log('Downloading survey responses for:', surveyId);
  };

  const handleViewDetails = (survey) => {
    setSelectedSurvey(survey);
    setDetailsDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mt: 0,
          mb: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '32px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3,
            fontWeight: 700,
            color: '#2d3748'
          }}
        >
          История опросов
        </Typography>

        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ 
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: 600,
              borderRadius: '16px',
              textTransform: 'none',
              fontSize: '0.95rem',
              color: '#718096',
              '&.Mui-selected': {
                color: '#0984e3',
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px',
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            }
          }}
        >
          <Tab label="Все опросы" value="all" />
          <Tab label="Одобренные" value="approved" />
          <Tab label="Отклонённые" value="rejected" />
          <Tab label="На проверке" value="pending" />
        </Tabs>

        {filteredSurveys.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              borderRadius: '24px',
              border: '1px solid rgba(116, 185, 255, 0.5)',
            }}
          >
            <Typography sx={{ color: '#fff', fontSize: '1rem', fontWeight: 500 }}>
              Не найдено опросов в этой категории
            </Typography>
          </Paper>
        ) : (
          <TableContainer 
            sx={{ 
              borderRadius: '24px',
              overflow: 'hidden'
            }}
          >
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)' }}>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Название опроса</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Дата завершения</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Статус</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Вознаграждение</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSurveys.map((survey) => (
                <TableRow 
                  key={survey.id}
                  sx={{
                    '&:hover': {
                      background: 'rgba(116, 185, 255, 0.05)',
                    },
                    transition: 'background 0.2s'
                  }}
                >
                  <TableCell sx={{ color: '#2d3748', fontWeight: 500 }}>
                    {survey.surveyTitle || 'Без названия'}
                  </TableCell>
                  <TableCell sx={{ color: '#718096' }}>
                    {survey.completedAt 
                      ? (() => {
                          const date = new Date(survey.completedAt);
                          return isNaN(date.getTime()) ? 'Н/Д' : date.toLocaleDateString('ru-RU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        })()
                      : 'Н/Д'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={statusLabels[survey.status] || 'На проверке'}
                      color={['approved', 'completed', 'rejected'].includes(survey.status) ? undefined : (statusColors[survey.status] || 'warning')}
                      size="small"
                      sx={{
                        borderRadius: '16px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        ...(['approved', 'completed'].includes(survey.status) && {
                          background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                          color: '#fff',
                          border: '1px solid rgba(45, 122, 31, 0.3)',
                        }),
                        ...(survey.status === 'rejected' && {
                          background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                          color: '#fff',
                          border: '1px solid rgba(214, 48, 49, 0.3)',
                        })
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#2d3748', fontWeight: 600 }}>
                    {(() => {
                      const r = survey.surveyReward || {};
                      const money = typeof r.moneyAmount === 'number' ? r.moneyAmount
                                    : (r.type === 'money' ? (r.amount || 0) : (r.money?.amount || 0));
                      const points = typeof r.pointsAmount === 'number' ? r.pointsAmount
                                     : (r.type === 'points' ? (r.amount || 0) : (r.points?.amount || 0));
                      const parts = [];
                      if (money > 0) parts.push(`${money} BYN`);
                      if (points > 0) parts.push(`${Number(points).toFixed(1)} баллов`);
                      return parts.length > 0 ? parts.join(' + ') : 'Н/Д';
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    {survey.status === 'rejected' ? (
                      <Tooltip title="Причина отклонения">
                        <IconButton 
                          sx={{
                            background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                            color: '#fff',
                            borderRadius: '12px',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #d63031 0%, #c0392b 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(214, 48, 49, 0.3)',
                            },
                            transition: 'all 0.2s'
                          }}
                          onClick={() => handleViewDetails(survey)}
                        >
                          <ErrorIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Подробнее">
                        <IconButton 
                          sx={{
                            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                            color: '#fff',
                            borderRadius: '12px',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(9, 132, 227, 0.3)',
                            },
                            transition: 'all 0.2s'
                          }}
                          onClick={() => handleViewDetails(survey)}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>

      <Dialog 
        open={detailsDialog} 
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '32px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        {selectedSurvey && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 700, 
              fontSize: '1.5rem',
              color: '#2d3748',
              pb: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              Детали опроса: {selectedSurvey.surveyTitle}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ 
                mb: 3,
                p: 2.5,
                background: 'rgba(116, 185, 255, 0.08)',
                borderRadius: '20px',
                border: '1px solid rgba(116, 185, 255, 0.2)'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#718096', mb: 1 }}>
                  Статус
                </Typography>
                <Chip 
                  label={statusLabels[selectedSurvey.status] || 'На проверке'}
                  color={['approved', 'completed', 'rejected'].includes(selectedSurvey.status) ? undefined : (statusColors[selectedSurvey.status] || 'warning')}
                  sx={{
                    borderRadius: '16px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    height: '36px',
                    ...(['approved', 'completed'].includes(selectedSurvey.status) && {
                      background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                      color: '#fff',
                      border: '1px solid rgba(45, 122, 31, 0.3)',
                    }),
                    ...(selectedSurvey.status === 'rejected' && {
                      background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                      color: '#fff',
                      border: '1px solid rgba(214, 48, 49, 0.3)',
                    })
                  }}
                />
              </Box>

              <Box sx={{ 
                mb: 3,
                p: 2.5,
                background: 'rgba(116, 185, 255, 0.08)',
                borderRadius: '20px',
                border: '1px solid rgba(116, 185, 255, 0.2)'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#718096', mb: 1 }}>
                  Дата завершения
                </Typography>
                <Typography sx={{ color: '#2d3748', fontWeight: 500 }}>
                  {selectedSurvey.completedAt 
                    ? (() => {
                        const date = new Date(selectedSurvey.completedAt);
                        return isNaN(date.getTime()) ? 'Н/Д' : date.toLocaleString('ru-RU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      })()
                    : 'Н/Д'}
                </Typography>
              </Box>

              <Box sx={{ 
                mb: 3,
                p: 2.5,
                background: 'rgba(116, 185, 255, 0.08)',
                borderRadius: '20px',
                border: '1px solid rgba(116, 185, 255, 0.2)'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#718096', mb: 1 }}>
                  Вознаграждение
                </Typography>
                <Typography sx={{ color: '#2d3748', fontWeight: 600, fontSize: '1.1rem' }}>
                  {(() => {
                    const r = selectedSurvey.surveyReward || {};
                    const money = typeof r.moneyAmount === 'number' ? r.moneyAmount
                                  : (r.type === 'money' ? (r.amount || 0) : (r.money?.amount || 0));
                    const points = typeof r.pointsAmount === 'number' ? r.pointsAmount
                                   : (r.type === 'points' ? (r.amount || 0) : (r.points?.amount || 0));
                    const parts = [];
                    if (money > 0) parts.push(`${money} BYN`);
                    if (points > 0) parts.push(`${Number(points).toFixed(1)} баллов`);
                    return parts.length > 0 ? parts.join(' + ') : 'Н/Д';
                  })()}
                </Typography>
              </Box>

              <Box sx={{ 
                mb: 3,
                p: 2.5,
                background: 'rgba(116, 185, 255, 0.08)',
                borderRadius: '20px',
                border: '1px solid rgba(116, 185, 255, 0.2)'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#718096', mb: 1 }}>
                  Затраченное время
                </Typography>
                <Typography sx={{ color: '#2d3748', fontWeight: 500 }}>
                  {selectedSurvey.timeSpent 
                    ? `${Math.floor(selectedSurvey.timeSpent / 60)} мин ${selectedSurvey.timeSpent % 60} сек` 
                    : 'Н/Д'}
                </Typography>
              </Box>

              {selectedSurvey.moderationComment && (
                <Box sx={{ 
                  mb: 3,
                  p: 2.5,
                  background: 'rgba(255, 118, 117, 0.08)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 118, 117, 0.3)'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#d63031', mb: 1 }}>
                    Комментарий модератора
                  </Typography>
                  <Typography sx={{ color: '#d63031', fontWeight: 500 }}>
                    {selectedSurvey.moderationComment}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button 
                onClick={() => setDetailsDialog(false)}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 16px rgba(9, 132, 227, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(9, 132, 227, 0.4)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                Закрыть
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}